import AttachFileIcon from '@mui/icons-material/AttachFile';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';
import type { FC } from 'react';

import type { ParameterSortingValuesE } from 'declarations/enums/tickets';
import type { Column } from 'declarations/types/common';

import TicketsReadStatusSelect from '../../TicketsActionDropDown/TicketsActionDropDown';
import {
  CHECKBOX_COLUMN_WIDTH_PX,
  COLUMN_LABEL_TO_SORT_PARAM,
  COLUMN_MIN_WIDTH_PX,
  NON_SORTABLE_COLUMN_LABELS,
  VIEWED_INDICATOR_COLUMN_WIDTH_PX,
  getDataColumnWidth,
} from '../TicketsTableConfiguration';

export interface TicketTableHeadProps {
  columnOrder: Column[];
  dataColumnWidthsPx: number[] | null;
  numSelected: number;
  rowCount: number;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  sortParameter: string;
  sortOrder: 'asc' | 'desc';
  onRequestSort: (param: ParameterSortingValuesE) => () => void;
  selectedTickets: string[];
  setSelectedTickets: (value: string[]) => void;
  totalWeight: number;
}

export const TicketTableHead: FC<TicketTableHeadProps> = ({
  columnOrder,
  dataColumnWidthsPx,
  numSelected,
  rowCount,
  onSelectAllClick,
  sortParameter,
  sortOrder,
  onRequestSort,
  selectedTickets,
  setSelectedTickets,
  totalWeight,
}) => {
  const visibleColumns = columnOrder.filter(c => c.visible !== false);
  const sortDirection = (sortOrder?.toLowerCase() ?? 'asc') as 'asc' | 'desc';

  return (
    <TableHead id="header-table-tickets">
      <TableRow
        sx={{
          backgroundColor: theme => theme.palette.background.default,
        }}
      >
        <TableCell
          padding="checkbox"
          style={{ width: CHECKBOX_COLUMN_WIDTH_PX, minWidth: CHECKBOX_COLUMN_WIDTH_PX }}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: theme => theme.palette.background.default,
            boxShadow: theme => `0 1px 0 0 ${theme.palette.divider}`,
            overflow: 'visible',
          }}
        >
          <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
            <Tooltip title="Number of tickets selected" arrow>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: 13, cursor: 'default' }}
              >
                {numSelected}
              </Typography>
            </Tooltip>
            <Checkbox
              color="primary"
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
              inputProps={{ 'aria-label': 'Select all tickets' }}
            />
          </Box>
        </TableCell>
        <TableCell
          style={{
            width: VIEWED_INDICATOR_COLUMN_WIDTH_PX,
            minWidth: VIEWED_INDICATOR_COLUMN_WIDTH_PX,
          }}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: theme => theme.palette.background.default,
            boxShadow: theme => `0 1px 0 0 ${theme.palette.divider}`,
            padding: '0 4px',
            verticalAlign: 'middle',
          }}
        >
          <TicketsReadStatusSelect
            selectedTickets={selectedTickets}
            setSelectedTickets={setSelectedTickets}
          />
        </TableCell>
        {visibleColumns.map((col, i) => {
          const sortParam = COLUMN_LABEL_TO_SORT_PARAM[col.label];
          const isSortable = sortParam && !NON_SORTABLE_COLUMN_LABELS.has(col.label);
          const isActive = sortParam && sortParameter === sortParam;
          const minWidthPx = COLUMN_MIN_WIDTH_PX[col.label] ?? 56;
          const widthStyle =
            dataColumnWidthsPx != null && dataColumnWidthsPx[i] != null
              ? { width: dataColumnWidthsPx[i], minWidth: minWidthPx }
              : { width: getDataColumnWidth(col.label, totalWeight), minWidth: minWidthPx };

          const attachmentIcon = (
            <Tooltip title="Attachment">
              <AttachFileIcon fontSize="small" aria-label="Attachment" />
            </Tooltip>
          );

          let cellContent: React.ReactNode;
          if (col.label === 'Attachment') {
            cellContent =
              isSortable && sortParam ? (
                <TableSortLabel
                  active={isActive}
                  direction={isActive ? sortDirection : 'asc'}
                  onClick={onRequestSort(sortParam)}
                >
                  {attachmentIcon}
                  {isActive ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortDirection === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              ) : (
                attachmentIcon
              );
          } else if (isSortable && sortParam) {
            cellContent = (
              <TableSortLabel
                active={isActive}
                direction={isActive ? sortDirection : 'asc'}
                onClick={onRequestSort(sortParam)}
              >
                {col.label}
                {isActive ? (
                  <Box component="span" sx={visuallyHidden}>
                    {sortDirection === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            );
          } else {
            cellContent = <Box component="span">{col.label}</Box>;
          }

          return (
            <TableCell
              key={col.id}
              align={col.label === 'P' ? 'center' : 'left'}
              sortDirection={isActive ? sortDirection : false}
              style={widthStyle}
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 100,
                backgroundColor: theme => theme.palette.background.default,
                boxShadow: theme => `0 1px 0 0 ${theme.palette.divider}`,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              }}
            >
              {cellContent}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
};
